import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import baseURL from "../../../Url/NodeBaseURL";

const ReceivablesTable = () => {
  const navigate = useNavigate();
  const [receivables, setReceivables] = useState([]);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    const fetchReceivables = async () => {
      try {
        const response = await fetch(`${baseURL}/get-unique-repair-details`);
        const data = await response.json();

        const filteredData = data.filter(
          (item) =>
            item.transaction_status === "Sales" ||
            item.transaction_status === "ConvertedInvoice"
        );

        setAllData(filteredData); // Save full data for detail view

        const grouped = new Map();

        filteredData.forEach((item) => {
          const key = item.customer_id;
          const bal_amt = Number(item.bal_amt) || 0;
          const bal_after_receipts = Number(item.bal_after_receipts) || 0;
          const receipts_amt = Number(item.receipts_amt) || 0;

          let balance = 0;
          if (bal_amt === receipts_amt) {
            balance = bal_after_receipts || 0;
          } else {
            balance = bal_after_receipts ? bal_after_receipts : bal_amt || 0;
          }

          if (grouped.has(key)) {
            grouped.get(key).balanceAmount += balance;
          } else {
            grouped.set(key, {
              customerId: key,
              accountName: (item.account_name || "").toUpperCase(),
              balanceAmount: balance,
            });
          }
        });

        const sorted = Array.from(grouped.values()).sort(
          (a, b) => b.balanceAmount - a.balanceAmount
        );

        setReceivables(sorted);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch receivables");
      } finally {
        setLoading(false);
      }
    };

    fetchReceivables();
  }, []);

  const toggleExpand = (customerId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [customerId]: !prev[customerId],
    }));
  };

  const calculateBalance = (row) => {
    const bal_amt = Number(row.bal_amt) || 0;
    const bal_after_receipts = Number(row.bal_after_receipts) || 0;
    const receipts_amt = Number(row.receipts_amt) || 0;
    let finalBalance;
    if (bal_amt === receipts_amt) {
      finalBalance = bal_after_receipts || 0;
    } else {
      finalBalance = bal_after_receipts ? bal_after_receipts : bal_amt || 0;
    }
    return finalBalance.toFixed(2);
  };

  const calculateDelayDays = (createdDateStr, balance) => {
    if (Number(balance) === 0) return 0;
    const dueDate = new Date(createdDateStr);
    const now = new Date();
    const diffTime = now - dueDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Function to handle click on receivable row and navigate to sales page
  const handleReceivableClick = async (customerId, accountName) => {
    try {
      // Find the first invoice for this customer to use for navigation
      const customerInvoices = allData.filter(
        (d) => d.customer_id === customerId
      );

      if (customerInvoices.length === 0) {
        console.error("No invoices found for this customer");
        return;
      }

      // Get the first invoice with balance > 0
      const firstInvoice = customerInvoices.find(
        (inv) => Number(calculateBalance(inv)) > 0
      );

      if (!firstInvoice) {
        console.error("No invoices with balance found for this customer");
        return;
      }

      const invoice_number = firstInvoice.invoice_number;
      const mobile = firstInvoice.mobile;

      // Generate tabId (same as SalesTable.js)
      const tabId = crypto.randomUUID();

      // Fetch repair details
      const repairResponse = await axios.get(`${baseURL}/get-repair-details/${invoice_number}`);
      const repairDetails = repairResponse.data;

      if (!repairDetails || !repairDetails.repeatedData) {
        console.error('No repeatedData found in response:', repairDetails);
        alert('No repair details found for the provided invoice number.');
        return;
      }

      // Filter repeatedData to include only items with specific transaction statuses
      const filteredRepairData = repairDetails.repeatedData.filter(
        (item) => item.transaction_status === "Sales" || item.transaction_status === "ConvertedInvoice" || item.transaction_status === "ConvertedRepairInvoice"
      );

      if (filteredRepairData.length === 0) {
        alert('No records found with the required transaction status.');
        return;
      }

      // Check if order_number exists; otherwise, use invoice_number
      const order_number = filteredRepairData[0]?.order_number || invoice_number;

      // Fetch old items details using order_number if available; otherwise, use invoice_number
      const oldItemsResponse = await axios.get(`${baseURL}/get/olditems/${order_number}`);
      const oldItemsDetails = oldItemsResponse.data;

      // Get today's date in yyyy-mm-dd format
      const today = new Date().toISOString().split('T')[0];

      // Update repair and old items details with today's date
      const formattedRepairDetails = filteredRepairData.map((item) => ({
        ...item,
        date: today,
        invoice_number,
      }));

      const formattedOldItems = oldItemsDetails.map((item) => ({
        ...item,
        date: today,
        invoice_number,
      }));

      // Store in localStorage (same pattern as SalesTable.js)
      localStorage.setItem(`repairDetails_${tabId}`, JSON.stringify(formattedRepairDetails));
      localStorage.setItem(`oldTableData_${tabId}`, JSON.stringify(formattedOldItems));

      // Set default payment details (same as SalesTable.js)
      const paymentDetails = {
        cash_amount: 0,
        card_amt: 0,
        chq_amt: 0,
        online_amt: 0,
      };

      localStorage.setItem(`paymentDetails_${tabId}`, JSON.stringify(paymentDetails));

      // Set discount percentage in localStorage if available
      if (formattedRepairDetails.length > 0 && formattedRepairDetails[0].disscount_percentage) {
        localStorage.setItem(`discount_${tabId}`, formattedRepairDetails[0].disscount_percentage);
      }

      console.log('Updated sales details added to localStorage:', formattedRepairDetails);

      // Navigate to the sales page with state (same pattern as SalesTable.js)
      navigate(`/sales?tabId=${tabId}`, {
        state: {
          invoice_number,
          mobile,
          cash_amount: 0,
          card_amt: 0,
          chq_amt: 0,
          online_amt: 0,
          repairDetails: formattedRepairDetails,
        },
      });

    } catch (error) {
      console.error('Error fetching sales details:', error);
      alert('Error fetching sales details. Please try again.');
    }
  };

  if (loading)
    return <div style={{ textAlign: "center", padding: 10 }}>Loading...</div>;
  if (error)
    return (
      <div style={{ color: "red", textAlign: "center", padding: 10 }}>
        {error}
      </div>
    );

  return (
    <div
      style={{
        maxHeight: "200px",
        overflowY: "auto",
        width: "100%",
        marginTop: "8px",
      }}
    >
      <table
        style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}
      >
        <thead>
          <tr
            style={{ backgroundColor: "#f5f5f5", position: "sticky", top: 0 }}
          >
            <th
              style={{
                padding: "6px 10px",
                textAlign: "left",
                fontWeight: "600",
                color: "#1976d2",
              }}
            >
              Account Name
            </th>
            <th
              style={{
                padding: "6px 10px",
                textAlign: "right",
                fontWeight: "600",
                color: "#1976d2",
              }}
            >
              Receivables
            </th>
          </tr>
        </thead>
        <tbody>
          {receivables.map((item, index) => {
            const isExpanded = expandedRows[item.customerId];
            const customerInvoices = allData.filter(
              (d) => d.customer_id === item.customerId
            );
            return (
              <React.Fragment key={index}>
                <tr
                  style={{ 
                    borderBottom: "1px solid #eee", 
                    cursor: "pointer",
                    transition: "background-color 0.2s"
                  }}
                  onClick={(e) => {
                    // Prevent navigation when clicking on expand/collapse arrow
                    if (e.target.textContent.includes("▼") || e.target.textContent.includes("▶")) {
                      toggleExpand(item.customerId);
                    } else {
                      handleReceivableClick(item.customerId, item.accountName);
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.target.parentElement.style.backgroundColor = "#f8f9fa";
                  }}
                  onMouseLeave={(e) => {
                    e.target.parentElement.style.backgroundColor = "transparent";
                  }}
                >
                  <td
                    style={{
                      padding: "6px 10px",
                      textAlign: "left",
                      maxWidth: "120px",
                    }}
                  >
                    {isExpanded ? "▼" : "▶"} {item.accountName}
                  </td>
                  <td
                    style={{
                      padding: "6px 10px",
                      textAlign: "right",
                      color: "#2e7d32",
                      fontWeight: "500",
                    }}
                  >
                    {new Intl.NumberFormat("en-IN", {
                      style: "currency",
                      currency: "INR",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                      .format(item.balanceAmount)
                      .replace(/^[^\d]+/, "")}
                  </td>
                </tr>

                {isExpanded && (
                  <tr>
                    <td colSpan={2} style={{ padding: "0px 10px" }}>
                      <table
                        style={{
                          width: "100%",
                          fontSize: "12px",
                          backgroundColor: "#f9f9f9",
                          marginTop: "5px",
                        }}
                      >
                        <thead>
                          <tr style={{ borderBottom: "1px solid #ccc" }}>
                            <th style={{ textAlign: "left", padding: "6px" }}>
                              Invoice Number
                            </th>
                            <th style={{ textAlign: "right", padding: "6px" }}>
                              Bal Amt (₹)
                            </th>
                            <th style={{ textAlign: "right", padding: "6px" }}>
                              Due Date
                            </th>
                            <th style={{ textAlign: "right", padding: "6px" }}>
                              Delayed (Days)
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {customerInvoices
                            .filter((inv) => Number(calculateBalance(inv)) > 0)
                            .map((inv, i) => (
                              <tr 
                                key={`inv-${i}`}
                                style={{ 
                                  cursor: "pointer",
                                  transition: "background-color 0.2s"
                                }}
                                onClick={() => handleReceivableClick(item.customerId, item.accountName)}
                                onMouseEnter={(e) => {
                                  e.target.parentElement.style.backgroundColor = "#e9ecef";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.parentElement.style.backgroundColor = "transparent";
                                }}
                              >
                                <td style={{ padding: "6px" }}>
                                  {inv.invoice_number}
                                </td>
                                <td
                                  style={{ padding: "6px", textAlign: "right" }}
                                >
                                  {calculateBalance(inv)}
                                </td>
                                <td
                                  style={{ padding: "6px", textAlign: "right" }}
                                >
                                  {new Date(
                                    inv.created_at
                                  ).toLocaleDateString()}
                                </td>
                                <td
                                  style={{ padding: "6px", textAlign: "right" }}
                                >
                                  {calculateDelayDays(
                                    inv.created_at,
                                    calculateBalance(inv)
                                  )}{" "}
                                  days
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ReceivablesTable;
